import { PRODUCT_NAME } from './brand';
// ============================================================================
// Phase 4/5 comms copy — DRAFT. NOT approved user-facing copy.
// ----------------------------------------------------------------------------
// Owner's instruction: "先自己寫一個,後續產品和老板體驗後還要修改的" — write our
// own draft first; product and the boss will revise this copy after hands-on
// experience. Treat every string below as a proposal, not final wording.
//
// Flip gate: PHASE45_COMMS_ENABLED. While it is `false`, every surface that
// consumes this file renders exactly the copy it rendered before this draft
// existed — the banner returns null, and the gated strings below resolve to
// their legacy values. Do NOT set it to `true` without the Phase 4/5 entry
// gates, which cover backend, migration, checkout disablement and the
// MindVault app, not just this copy:
//   - docs/credit-wallet/04-phase-4-behavior-flip.md  -> "Entry gate"
//   - docs/credit-wallet/05-phase-5-credit-connections.md -> "Entry criteria"
//
// Facts this copy states (from the Phase 4/5 plan; 1 credit = HK$5):
//   - Scribe sessions draw from the per-therapist credit wallet.
//   - Self-brought client connections cost 10 credits/month.
//   - Clients who book through MindVault connect free.
//   - Math: Basic 30 cr = 2 self-brought clients + 10 cr of sessions;
//     Pro 100 cr = 5 clients + 50 cr.
//   - No connection cap since recharge shipped: credit-paid connections are
//     limited only by wallet balance (10 credits each). A plan's allotment funds
//     an indicative count (Basic ~3, Pro ~10, Free 1) but recharge extends it.
//   - Before the recharge phase ships there is NO mid-cycle refill — the only
//     answers to an empty wallet are upgrade or renewal. This copy does NOT
//     promise recharge packs.
//
// i18n note: this repo uses English inline strings (no locale files); these
// drafts follow that convention.
// ============================================================================

// Single flip gate for all Phase 4/5 comms surfaces. Keep `false` until the
// entry gates above are met.
export const PHASE45_COMMS_ENABLED = true;

// No flip-date announcement: Scribe launched as a new app on the credit
// wallet (2026-08-28), so there is no "before" state to announce a switch
// from. The former Phase45CommsBanner and the {FLIP_DATE} token were removed
// with that decision.

// All Phase 4/5 comms copy in one place so product/owner can review it as a
// set. `legacy` values are the current live strings, kept here (instead of
// being deleted) so the flip diff is the single flag; they must stay
// byte-identical to what shipped before this draft. Delete the `legacy`
// branches in the same commit that enables the flag.
export const PHASE45_COPY = {
  // MINDVAULT_PLAN_NOTE replacement (renders in Onboarding step 7). The old
  // "no per-client booking or access code required" promise is retired by
  // Phase 5.
  planNote: {
    legacy:
      `Good to know: You can connect ${PRODUCT_NAME} with MindVault on the Free plan. However, the ` +
      `Sharing function on ${PRODUCT_NAME} and the Practice button in MindVault will be disabled until ` +
      'a paid subscription is in place. Basic and Pro include MindVault access to all your ' +
      'clients — no per-client booking or access code required (clients still control sharing).',
    flipped:
      `Good to know: You can connect ${PRODUCT_NAME} with MindVault on the Free plan. Clients who ` +
      'book you through MindVault connect free; self-brought client ' +
      'connections cost 10 credits/month, drawn from your credit wallet — Basic\'s 30 ' +
      'credits cover 2 self-brought clients + 10 credits of sessions, Pro\'s 100 credits ' +
      'cover 5 clients + 50 credits. There is no connection cap — connect as many ' +
      'credit-paid clients as your wallet balance covers, and recharge for more.',
  },

  // D-010 plan-card connection line (BASIC/PRO features). "Keep and polish":
  // same fact (10 credits/month each), now with the show-the-math arithmetic.
  planCardConnectionLines: {
    basicLegacy: 'Connect credit-paid clients at 10 credits/month each (no cap — limited only by your balance)',
    basicFlipped:
      'Credit-paid clients at 10 credits/month each — no cap; your 30 credits cover 2 self-brought clients + 10 credits of sessions, recharge for more',
    proLegacy: 'Connect credit-paid clients at 10 credits/month each (no cap — limited only by your balance)',
    proFlipped:
      'Credit-paid clients at 10 credits/month each — no cap; your 100 credits cover 5 clients + 50 credits of sessions, recharge for more',
  },

  // AccessCodesSection.js intro paragraph — the "gives you unrestricted Scribe
  // use for that client" claim becomes false at flip; connected clients then
  // draw Scribe sessions from the wallet (matching planNote.flipped).
  connectionsParagraph: {
    legacy:
      'A monthly access code connects you to a MindVault client who has not booked ' +
      'you through MindVault. The connection goes live once the client consents to ' +
      `sharing in their MindVault app, and gives you unrestricted ${PRODUCT_NAME} use for ` +
      'that client. Cancelling only severs the connection — the client keeps their ' +
      'account and data.',
    flipped:
      'A monthly access code connects you to a MindVault client who has not booked ' +
      'you through MindVault. The connection goes live once the client consents to ' +
      `sharing in their MindVault app, and ${PRODUCT_NAME} sessions for that client draw from ` +
      'your credit wallet. Cancelling only severs the connection — the client keeps ' +
      'their account and data.',
  },
};
